import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { useUserOrg } from '../lib/org';
import { getSite, listActivePermits, listAllPermits, type Site, type PermitRow } from '../lib/db';
import { PERMIT_TEMPLATES } from '../data/permitTemplates';
import { LoadingIndicator } from '../components/LoadingIndicator';

type Tab = 'active' | 'history';

function statusPill(p: PermitRow): { label: string; tone: string } {
  if (p.status === 'closed')   return { label: 'Closed',    tone: 'ok' };
  if (p.status === 'expired')  return { label: 'Expired',   tone: 'danger' };
  if (p.status === 'cancelled')return { label: 'Cancelled', tone: 'pending' };
  if (new Date(p.expires_at) < new Date()) return { label: 'Overdue', tone: 'danger' };
  return { label: p.status === 'open' ? 'Open' : 'Active', tone: 'warn' };
}

export function Permits() {
  const { siteId } = useParams<{ siteId: string }>();
  const { org } = useUserOrg();
  const [tab, setTab] = useState<Tab>('active');
  const [site, setSite] = useState<Site | null>(null);
  const [active, setActive] = useState<PermitRow[]>([]);
  const [history, setHistory] = useState<PermitRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!siteId || !org) return;
    setLoading(true);
    setError(null);
    Promise.all([getSite(siteId), listActivePermits(siteId), listAllPermits(siteId)])
      .then(([s, a, h]) => {
        setSite(s);
        setActive(a);
        setHistory(h.filter((p) => !['open', 'active'].includes(p.status)));
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [siteId, org]);

  const list = tab === 'active' ? active : history;

  return (
    <AppShell title="Permits" back={`/sites/${siteId}`}>
      {site && (
        <div className="card" style={{ marginBottom: 12 }}>
          <p style={{ margin: 0 }}><strong>{site.name}</strong></p>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
            Permits-to-Work issued for this site
          </p>
        </div>
      )}

      <div className="seg-tabs" role="tablist" aria-label="Permit list">
        <button
          type="button" role="tab" aria-selected={tab === 'active'}
          className={`seg-tab ${tab === 'active' ? 'active' : ''}`}
          onClick={() => setTab('active')}
        >
          Active ({active.length})
        </button>
        <button
          type="button" role="tab" aria-selected={tab === 'history'}
          className={`seg-tab ${tab === 'history' ? 'active' : ''}`}
          onClick={() => setTab('history')}
        >
          History ({history.length})
        </button>
      </div>

      <h3 className="section-title" style={{ marginTop: 16 }}>Issue a new permit</h3>
      <div className="permit-templates" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
        {PERMIT_TEMPLATES.map((t) => (
          <Link
            key={t.id}
            to={`/sites/${siteId}/permits/new/${t.id}`}
            className="card permit-template-card"
            style={{ textDecoration: 'none', textAlign: 'center', padding: 12 }}
          >
            <strong style={{ display: 'block' }}>{t.title}</strong>
            <span className="muted" style={{ fontSize: 11 }}>
              {t.fireWatchMinutes ? `${t.fireWatchMinutes} min watch` : `${t.defaultDurationHours}h validity`}
            </span>
          </Link>
        ))}
      </div>

      {error && <div className="banner banner-error" style={{ marginTop: 12 }}>{error}</div>}
      {loading && <LoadingIndicator />}

      <h3 className="section-title" style={{ marginTop: 22 }}>{tab === 'active' ? 'Active permits' : 'History'}</h3>

      <div className="doc-list">
        {list.map((p) => {
          const pill = statusPill(p);
          return (
            <div key={p.id} className="card" style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <strong>{p.title}</strong>
                    <span className={`status-pill status-${pill.tone}`}>{pill.label}</span>
                  </div>
                  <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
                    Issued to <strong>{p.issued_to_name}</strong>
                    {p.issued_to_company ? ` (${p.issued_to_company})` : ''}
                  </p>
                  <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
                    Issued {new Date(p.issued_at).toLocaleString('en-GB')}
                    {' · '}expires {new Date(p.expires_at).toLocaleString('en-GB')}
                  </p>
                </div>
                <Link to={`/permits/${p.id}`} className="btn btn-ghost btn-sm">View</Link>
              </div>
            </div>
          );
        })}
        {!loading && list.length === 0 && (
          <p className="muted">{tab === 'active' ? 'No active permits.' : 'No permit history yet.'}</p>
        )}
      </div>
    </AppShell>
  );
}
